import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { Button, Col, Divider, Form, Row, Table, Typography } from 'antd';
import Decimal from 'decimal.js';

import { Breadcrumb, Card } from 'src/components/custom';
import { InputField } from 'src/components/form';
import useForm, { FormValue } from './useForm';
import useTable from './useTable';
import { NumberFormatter } from 'src/utils/number';
import history from 'src/libs/history';
import useOrder from 'src/queries/useOrder';
import GetWebMitrResponse = Response.GetWebMitrResponse;
import { Info } from 'src/components/icon';
import {
  DISPLAY_OF_TOTAL_AMOUNT,
  STATUS_CODE,
  UNCHIN_SETTEI_TYPE,
} from 'src/constants/app';
const GET_WEB_MITR = 'order/preorder';
import order from 'src/repositories/order';

const OrderContainer: React.FC = () => {
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [preorderData, setPreorderData] = useState<any>();
  const [isLoadingPreorder, setIsLoadingPreorder] = useState(false);
  const [preOrderId, setPreOrderId] = useState<string>('');
  const location = useLocation();
  const state = location?.state as any;
  const webMitrNo: string[] =
    state?.items?.map((item: any) => item.webMitrNo) || [];
  const { t } = useTranslation();

  const { formInfo } = useForm();
  const { control, handleSubmit } = formInfo;
  const { tableProps } = useTable(isLoadingPreorder, preorderData, control);
  const client = useQueryClient();

  const selectedItems = preorderData?.items || [];

  const handleRequestOrderPreorderSuccess = async (preorderId: string) => {
    try {
      setIsLoadingPreorder(true);
      const currentCurrencyRes = await client.fetchQuery<
        AxiosResponse<GetWebMitrResponse>
      >({
        queryKey: [`${GET_WEB_MITR}?preorderId=${preorderId}`],
        queryFn: order.getWebMitr,
      });
      setPreOrderId(preorderId);
      setPreorderData(currentCurrencyRes.data?.item);
    } catch (error) {
      console.error('Error fetching preorder data:', error);
    } finally {
      setIsLoadingPreorder(false);
    }
  };

  const handleRequestOrderSuccess = () => {
    setErrorMessage('');
    setSuccessMessage(t('order_page.order_completion'));
  };

  const handleRequestOrderFailed = (error: AxiosError<Response.Error>) => {
    // Reset success message
    setSuccessMessage('');

    const code = error?.response?.data.code;
    if (code) {
      // @ts-ignore
      const message = t(`message.${code}`);
      if (message !== `message.${code}`) {
        setErrorMessage(message as string);
      }
    }
  };
  const { requestOrderPreorder } = useOrder(handleRequestOrderPreorderSuccess);
  const { requestOrder } = useOrder(
    handleRequestOrderSuccess,
    handleRequestOrderFailed,
  );

  const numberOfRecord = preorderData?.items?.length || 0;

  const totals = preorderData?.items.reduce(
    (acc, item) => {
      return {
        marumeMitrGaku: acc.marumeMitrGaku.plus(item.marumeMitrGaku || 0),
        marumeSogaku: acc.marumeSogaku.plus(item.marumeSogaku || 0),
        marumeJury: acc.marumeJury.plus(item.marumeJury || 0),
      };
    },
    {
      marumeMitrGaku: new Decimal(0),
      marumeSogaku: new Decimal(0),
      marumeJury: new Decimal(0),
    },
  );

  const totalAmount = Number(totals?.marumeSogaku) || 0;
  const totalWeight = Number(totals?.marumeJury) || 0;
  const totalAmountInCludedTax = Number(totals?.marumeMitrGaku) || 0;
  const maxAmountAllow = preorderData?.jogenKingaku;
  const hasError = !!maxAmountAllow && maxAmountAllow < totalAmount;
  const messageCode = preorderData?.messageCode;

  const showPriceIncludingTax =
    preorderData?.kingakuHyoji === DISPLAY_OF_TOTAL_AMOUNT.TAX_INCLUDED;

  const showShippingFee =
    selectedItems?.[0]?.unchinSettei === UNCHIN_SETTEI_TYPE.TOTAL_TREATMENT;

  const request = (form: FormValue) => {
    if (hasError || !numberOfRecord) {
      return;
    }
    requestOrder.mutate({
      renraku: form.contact,
      tanto: form.orderPersonName,
      preOrderId,
      deliveries: form.deliveryDates
        ?.filter((item) => !!item.key)
        .map((item) => ({
          webMitrNo: item.key,
          kiboNoki: item.value,
        })),
      roundParam: preorderData?.roundParam,
      currency: preorderData?.currencyUnit,
    });
  };

  useEffect(() => {
    requestOrderPreorder.mutate({
      webMitrNoList: webMitrNo,
    });
  }, []);

  return (
    <div className="min-h-100 mb-30 mt-30 order-page">
      <Breadcrumb />
      <Card title={t('order_page.title')}>
        <Row>
          <Col xs={24} sm={24} md={12} lg={16}>
            <InputField<FormValue>
              controller={{ control, name: 'contact' }}
              inputProps={{
                placeholder: t('order_page.contact'),
                maxLength: 80,
              }}
              label={t('order_page.contact')}
              labelCol={{ xs: 24, sm: 24, md: 4 }}
              wrapperCol={{ xs: 24, sm: 24, md: 16 }}
            />
          </Col>
        </Row>
      </Card>
      <Row>
        <Col span={24}>
          <Table {...tableProps} className="quotation_table" />
        </Col>
      </Row>

      {messageCode === STATUS_CODE.CURRENCY_TRANSFER && (
        <Typography.Title level={5} className="noti">
          {t('message.W000002')}
        </Typography.Title>
      )}

      <Card title="" className="mt-16">
        <Form onFinish={handleSubmit((val) => request(val))}>
          <Row className="mt-16">
            <Col
              xs={24}
              sm={24}
              md={9}
              className="d-flex align-center margin-sm"
            >
              <Row className="d-flex-1">
                <Typography.Title level={3} className="sm-flex-1">
                  {t('order_page.total_record')}
                </Typography.Title>
                <Typography.Title className="mt-0 highlight" level={3}>
                  {numberOfRecord}&nbsp;
                </Typography.Title>
                <Typography.Title className="mt-0" level={3}>
                  {t('order_page.point')}
                </Typography.Title>
              </Row>
            </Col>
            <Divider className="divider" />
            <Col
              xs={24}
              sm={24}
              md={9}
              className="d-flex align-center margin-sm"
            >
              <Row className="d-flex-1">
                <Typography.Title level={3} className="sm-flex-1">
                  {t('order_page.gross_weight')}
                </Typography.Title>
                <Typography.Title className="mt-0 highlight" level={3}>
                  {NumberFormatter.format(totalWeight)}
                </Typography.Title>
                <Typography.Title className="mt-0 text-primary" level={3}>
                  {selectedItems?.[0]?.juryTani}
                </Typography.Title>
              </Row>
            </Col>
            <Divider className="divider" />
          </Row>
          <Row className="mt-16">
            <Col
              xs={24}
              sm={24}
              md={9}
              className="d-flex align-center margin-sm"
            >
              <Row className="d-flex-1">
                <Typography.Title level={3} className="sm-flex-1">
                  {t('order_page.total_price_exclude_tax')}
                </Typography.Title>
                <Typography.Title className="mt-0 highlight" level={3}>
                  {preorderData?.currencyUnit
                    ? `${NumberFormatter.format(totalAmount, 18)}(${
                        preorderData?.currencyUnit
                      })`
                    : `${NumberFormatter.format(totalAmount, 18)}`}
                </Typography.Title>
              </Row>
            </Col>
            <Divider className="divider" />
            {showPriceIncludingTax && (
              <Col
                xs={24}
                sm={24}
                md={9}
                className="d-flex align-center margin-sm"
              >
                <Row className="d-flex-1">
                  <Typography.Title level={3} className="sm-flex-1">
                    {t('order_page.total_price_include_tax')}
                  </Typography.Title>
                  <Typography.Title className="mt-0 highlight" level={3}>
                    {preorderData?.currencyUnit
                      ? `${NumberFormatter.format(
                          totalAmountInCludedTax,
                          18,
                        )}(${preorderData?.currencyUnit})`
                      : `${NumberFormatter.format(totalAmountInCludedTax, 18)}`}
                  </Typography.Title>
                </Row>
              </Col>
            )}
            <Divider className="divider" />
            {showShippingFee && (
              <Col
                xs={24}
                sm={24}
                md={6}
                className="d-flex align-center margin-sm"
              >
                <Row className="d-flex-1">
                  <Typography.Title level={3} className="sm-flex-1">
                    {t('order_page.fare')}
                  </Typography.Title>
                  <Typography.Title className="mt-0 highlight" level={3}>
                    {preorderData?.currencyUnit
                      ? `${NumberFormatter.format(
                          selectedItems?.[0]?.shoUnchn,
                          18,
                        )}(${preorderData?.currencyUnit})`
                      : `${NumberFormatter.format(
                          selectedItems?.[0]?.shoUnchn,
                          18,
                        )}`}
                  </Typography.Title>
                </Row>
              </Col>
            )}
            <Divider className="divider" />
          </Row>
          <Row className="mb-16">
            <Col xs={24} sm={24} md={24} className="margin-sm">
              <Row justify="end" gutter={20} className="included_tax">
                <Col xs={10} md={5}>
                  <Button
                    type="default"
                    className="btn-back w-full"
                    onClick={() =>
                      history.push(`/quotation${state?.search || ''}`, {
                        selectedItems,
                      })
                    }
                  >
                    {t('order_page.button.back')}
                  </Button>
                </Col>
                <Col xs={10} md={5}>
                  <Button
                    type="primary"
                    className="w-full"
                    htmlType="submit"
                    loading={requestOrder.isLoading}
                    disabled={!webMitrNo.length}
                  >
                    {t('order_page.button.order')}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Card>
      {hasError && (
        <Typography.Text
          className="ant-form-item-explain ant-form-item-explain-error"
          type="danger"
          ellipsis={{ tooltip: true }}
        >
          <Info />
          {t('message.E000065')}
        </Typography.Text>
      )}
      {errorMessage && (
        <Typography.Text
          className="ant-form-item-explain ant-form-item-explain-error error_message"
          type="danger"
          ellipsis={{ tooltip: true }}
        >
          <Info />
          {errorMessage}
        </Typography.Text>
      )}
      {successMessage && (
        <Typography.Text
          className="ant-form-item-explain ant-form-item-explain-error"
          type="danger"
          ellipsis={{ tooltip: true }}
        >
          {successMessage}
        </Typography.Text>
      )}
      <div className="notes">
        <Typography.Title level={5}>{t('order_page.notes.1')}</Typography.Title>
      </div>
    </div>
  );
};

export default OrderContainer;
